import React, { useEffect } from "react"
import { graphql } from "gatsby"
import { HelmetDatoCms } from "gatsby-source-datocms"
import Layout from "@layouts/layout"
import {
  Intro,
  Banner,
  Block,
  Container,
  Disclaimer,
  FilteredList,
  Cards,
  Card
} from "@components"
import JacuzziHero from "../partials/jacuzzi/hero"
import JacuzziFeaturedCard from "../partials/jacuzzi/featured-card"
import { transformProductToCard } from "@helpers/productHelpers"
import { removeDuplicates, configTagsForSelect } from "@helpers/miscHelpers"
import ModuleArea from "../helpers/moduleArea"
import { gtmPush } from "@helpers/gtmHelpers"
import moment from "moment"
import { JsonLd } from "react-schemaorg"
import { shouldNoIndex } from "../helpers/noIndexPaths"

const ProductCollection = ({ location, pageContext, data }) => {
  const {
    name,
    leadNode,
    lead,
    path,
    seoMetaTags,
    products,
    showPriceDisclaimer,
    tag,
    banner,
    datoContent,
    collectionsGrid,
    originalId,
    id,
    showPathways
  } = data.collection

  const noIndex = shouldNoIndex(path)

  const disclaimer = data.globalConfig.priceDisclaimerNode
  const allPathways = data.globalConfig.defaultPathways.slice(0, 3)

  const tagType = tag?.__typename || ""

  const introConfig = {
    breadcrumbs: pageContext.breadcrumbs,
    title: name,
    lead: leadNode.childMarkdownRemark.html
  }

  const productTags = ["brand", "price", "usage", "size"]

  // Get customTags from the collection (array of { name, slug })
  const collectionCustomTags = data.collection.customTags || []

  // Build filters
  let filters = []

  // Helper function to get default filters
  const getDefaultFilters = () => {
    const allTagsInCollection = products
      .map(product =>
        productTags.filter(tag => product[tag]).map(tag => product[tag])
      )
      .flat(2)

    const tagsInCollection = removeDuplicates(allTagsInCollection)

    const getTags = tagWanted => {
      if (tagType.toLowerCase().includes(tagWanted)) return []
      return tagsInCollection.filter(({ model }) => model.apiKey === tagWanted)
    }

    const orderTags = array =>
      array.sort((a, b) => a.name.localeCompare(b.name))

    const collectionBrands = orderTags([...getTags("brand")])
    const collectionPrices = orderTags([...getTags("price")])
    const collectionSizes = orderTags([...getTags("size")])
    const collectionUsages = orderTags([...getTags("usage")])

    const defaultFilters = []

    collectionBrands.length > 1 &&
      defaultFilters.push({
        name: "brand",
        label: "brand",
        placeholder: {
          text: "Select brand"
        },
        options: [...configTagsForSelect(collectionBrands)]
      })

    collectionPrices.length > 1 &&
      defaultFilters.push({
        name: "price",
        label: "price",
        placeholder: {
          text: "Select price range"
        },
        options: [...configTagsForSelect(collectionPrices)]
      })

    collectionSizes.length > 1 &&
      defaultFilters.push({
        name: "size",
        label: "size",
        placeholder: {
          text: "Select seat number"
        },
        options: [...configTagsForSelect(collectionSizes)]
      })

    collectionUsages.length > 1 &&
      defaultFilters.push({
        name: "usage",
        label: "usage",
        placeholder: {
          text: "Select usage"
        },
        options: [...configTagsForSelect(collectionUsages)]
      })

    return defaultFilters
  }

  if (collectionCustomTags.length > 0) {
    // For each custom tag, build a filter with unique values from products
    const customFilters = collectionCustomTags.map(tag => {
      // Gather all values for this tag from all products
      const values = products
        .map(product =>
          (product.customTags || [])
            .filter(t => t.tag && t.tag.slug === tag.slug)
            .map(t => t.value)
        )
        .flat()
        .filter(Boolean)
      // Remove duplicates
      const uniqueValues = Array.from(new Set(values))

      // Convert to same format as configTagsForSelect
      const options = uniqueValues.map(value => ({
        value: value,
        text: value
      }))

      return {
        name: tag.slug,
        label: tag.name,
        placeholder: { text: `Select ${tag.name}` },
        options: options
      }
    })

    // Add custom filters
    filters = [...customFilters]

    // For 'saunas' collection, also add brand filter if available
    if (data.collection.slug === "saunas") {
      const defaultFilters = getDefaultFilters()
      const brandFilter = defaultFilters.find(filter => filter.name === "brand")
      if (brandFilter) {
        filters.push(brandFilter)
      }
    }
  } else {
    // Default filters if no custom tags
    filters = getDefaultFilters()
  }

  const configuredProducts =
    products.length > 0 &&
    products
      .map(product => transformProductToCard(product))
      .sort((a, b) => a?.pricing?.price - b?.pricing?.price)

  const isJacuzzi = data.collection.jacuzziFeatureCollection

  const remarketCategories = [
    "Saunas",
    "Pergolas",
    "Umbrellas",
    "Outdoor Fires",
    "Spa Pools",
    "Plunge Pools",
    "Swim Spas",
    "Family Spa",
    "Entertainer"
  ]
  const remarketBrands = ["Jacuzzi", "Fisher", "Vortex"]
  if (remarketCategories.some(el => name.includes(el))) {
    gtmPush({
      event: "remarketing",
      remarketing: {
        product_id: "0",
        brand: "0",
        product_category: name
      }
    })
  } else if (remarketBrands.some(el => name.includes(el))) {
    gtmPush({
      event: "remarketing",
      remarketing: {
        product_id: "0",
        brand: name,
        product_category: "0"
      }
    })
  }

  useEffect(() => {
    const ecommerceItem = []
    if (products.length > 0) {
      products.forEach(item => {
        const isPromoCurrent = moment().isBetween(
          moment(item.netoProduct.promotionStartDate),
          moment(item.netoProduct.promotionExpiryDate)
        )
        ecommerceItem.push({
          item_id: item.netoProduct?.sku,
          item_name: item.name,
          affiliation: "Spa World",
          item_brand: item.brand?.name,
          item_category: pageContext?.treeParent?.title,
          item_variant: "N/A",
          item_list_name: name,
          item_list_id: name?.toLowerCase().replaceAll(" ", "-"),
          price: isPromoCurrent
            ? item.netoProduct?.retailPrice
            : item.netoProduct?.promotionPrice,
          currency: item.netoProduct?.currency,
          quantity: "1"
        })
      })

      gtmPush({ ecommerce: null })
      gtmPush({
        event: "view_item_list",
        ecommerce: {
          item_list_name: name,
          item_list_id: name.toLowerCase().replaceAll(" ", "-"),
          items: ecommerceItem
        }
      })
    }
  }, [products])

  const ecommerceOnClick = item => {
    const ecommerceItem = [
      {
        item_id: item.sku,
        item_name: item.heading,
        affiliation: "Spa World",
        item_brand: item.brand?.name,
        item_category: pageContext?.treeParent?.title,
        item_variant: "N/A",
        item_list_name: name,
        item_list_id: name?.toLowerCase().replaceAll(" ", "-"),
        price: item.pricing?.now,
        currency: item.pricing?.currency,
        quantity: "1"
      }
    ]

    gtmPush({ ecommerce: null })
    gtmPush({
      event: "select_item",
      ecommerce: {
        item_list_name: name,
        item_list_id: name,
        items: ecommerceItem
      }
    })
  }

  const schemas = generateSchemaMarkup(products, name, lead, path)

  return (
    <Layout isJacuzzi={isJacuzzi} hasNoIndex={noIndex}>
      <HelmetDatoCms defer={false} seo={seoMetaTags} />

      {isJacuzzi && (
        <JacuzziHero
          title={name}
          caption={
            data.collection.jacuzziHeroTextNode
              ? data.collection.jacuzziHeroTextNode.childMarkdownRemark
              : ""
          }
          image={data.collection.jacuzziHeroImage}
          cta={data.collection.jacuzziHeroLink}
          breadcrumbs={pageContext.breadcrumbs}
        />
      )}

      {!isJacuzzi && (
        <Block>
          <Intro {...introConfig} banner={banner} />
        </Block>
      )}

      {collectionsGrid?.length > 0 && (
        <Block padding="double-bottom">
          <ModuleArea modules={collectionsGrid} location={location} />
        </Block>
      )}

      <Container>
        <div id="products" name="products" />
        {configuredProducts.length > 0 ? (
          <FilteredList
            items={[...configuredProducts]}
            filters={filters}
            defaultQueries={new Map()}
            location={location}
            onCardClick={ecommerceOnClick}
            itemsVisible={50}
            isCollection
            listSchema={schemas.list}
          />
        ) : (
          <center>
            Sorry, there are currently no available products in this collection.
          </center>
        )}
        {isJacuzzi && data.collection.jacuzziBannerText && (
          <Block gutters padding="double-top-single-bottom">
            <JacuzziFeaturedCard
              heading={data.collection.jacuzziBannerHeading}
              caption={data.collection.jacuzziBannerText}
              image={data.collection.jacuzziBannerImage}
              link={data.collection.jacuzziBannerLink}
              id="1"
            />
          </Block>
        )}
      </Container>
      {!!datoContent && (
        <Block padding="double-top-single-bottom">
          <ModuleArea modules={datoContent} location={location} />
        </Block>
      )}
      {showPathways && (
        <Block padding="both" gutters>
          <Cards>
            {allPathways.map(pathway => {
              const { link, displayText, url } = pathway.link
              return (
                <Card
                  key={`pathway-${pathway.id}`}
                  {...pathway}
                  link={{
                    path: link && link.path,
                    title: displayText,
                    url: url && url
                  }}
                  pathway
                  elevated
                  truncate={3}
                />
              )
            })}
          </Cards>
        </Block>
      )}
      {showPriceDisclaimer && (
        <Block gutters padding="both">
          <Container maxWidth={"inner"}>
            <Disclaimer content={disclaimer} width={"small"} />
          </Container>
        </Block>
      )}
      <JsonLd item={schemas.collection} />
    </Layout>
  )
}

const generateSchemaMarkup = (
  products,
  collectionName,
  collectionDescription,
  collectionUrl
) => {
  const itemListElement = products.map((product, index) => ({
    "@type": "ListItem",
    position: index + 1,
    url: product.path
  }))

  // Enhanced Collection schema with both CollectionPage and Collection
  const collectionSchema = {
    "@context": "http://schema.org",
    "@type": "CollectionPage",
    name: collectionName,
    description: collectionDescription,
    url: collectionUrl,
    // Add Collection type for better semantic markup
    mainEntity: {
      "@type": "Collection",
      name: collectionName,
      description: collectionDescription,
      url: collectionUrl
    }
  }

  // Generate List schema for the products
  const listSchema = {
    "@context": "http://schema.org",
    "@type": "ItemList",
    name: `${collectionName} Products`,
    description: `List of products in ${collectionName}`,
    numberOfItems: products.length,
    itemListElement: products.map((product, index) => {
      const imageUrl = product.heroImage?.gatsbyImageData?.images?.fallback?.src

      return {
        "@type": "ListItem",
        position: index + 1,
        item: {
          "@type": "Product",
          name: product.name,
          url: product.path,
          sku: product.netoProduct?.sku,
          brand: product.brand?.name,
          image: imageUrl,
          description: product.description,
          offers: {
            "@type": "Offer",
            price: product.netoProduct?.retailPrice,
            priceCurrency: product.netoProduct?.currency,
            availability: "http://schema.org/InStock"
          }
        }
      }
    })
  }

  return {
    collection: collectionSchema,
    list: listSchema
  }
}

export const query = graphql`
  query collectionById($id: String!, $language: String!) {
    collection: datoCmsCollection(locale: $language, id: { eq: $id }) {
      slug: newSlug
      name
      path
      id
      originalId
      showPriceDisclaimer
      lead
      leadNode {
        childMarkdownRemark {
          html
        }
      }
      collectionsGrid {
        ... on DatoCmsCollectionsGrid {
          ...CollectionsGrid
        }
      }
      banner {
        id
        link {
          ...Link
        }
        image {
          url
          gatsbyImageData(
            layout: FULL_WIDTH
            imgixParams: {
              h: "295"
              w: "1170"
              fit: "crop"
              q: 60
              auto: "format"
            }
          )
          alt
        }
        mobileImage {
          url
          gatsbyImageData(
            layout: FULL_WIDTH
            imgixParams: { h: "600", fit: "cover", q: 60, auto: "format" }
          )
          alt
        }
      }
      products {
        ...ProductCard
        customTags {
          tag {
            name
            slug
          }
          value
        }
      }
      customTags {
        name
        slug
      }
      parentCategory {
        id
        slug
        title
      }
      ...ProductCollectionModuleArea
      seoMetaTags {
        ...GatsbyDatoCmsSeoMetaTags
      }
      tag {
        ... on DatoCmsBrand {
          id
          name
          slug
          __typename
        }
        ... on DatoCmsUsage {
          id
          name
          slug
          __typename
        }
        ... on DatoCmsSize {
          id
          name
          slug
          __typename
        }
        ... on DatoCmsPrice {
          id
          name
          slug
          __typename
        }
        ... on DatoCmsTag {
          id
          name
          slug
          __typename
        }
      }
      image {
        gatsbyImageData
      }
      jacuzziFeatureCollection
      jacuzziHeroImage {
        gatsbyImageData(
          layout: FULL_WIDTH
          imgixParams: { w: "1600", h: "1080", fit: "crop", q: 70 }
        )
        alt
      }
      jacuzziHeroTextNode {
        childMarkdownRemark {
          html
        }
      }
      jacuzziHeroLink {
        displayText
        url
        locationHash
        link {
          ... on DatoCmsPage {
            id
            path
          }
          ... on DatoCmsProductLandingPage {
            id
            path
          }
          ... on DatoCmsProduct {
            id
            path
          }
          ... on DatoCmsCollection {
            id
            path
          }
          ... on DatoCmsBlogPost {
            id
            path
          }
        }
      }
      jacuzziBannerHeading
      jacuzziBannerText
      jacuzziBannerLink {
        displayText
        url
        locationHash
        link {
          ... on DatoCmsPage {
            id
            path
          }
          ... on DatoCmsProductLandingPage {
            id
            path
          }
          ... on DatoCmsProduct {
            id
            path
          }
          ... on DatoCmsCollection {
            id
            path
          }
          ... on DatoCmsBlogPost {
            id
            path
          }
        }
      }
      showPathways
    }

    globalConfig: datoCmsGlobal(locales: { eq: $language }) {
      contactPage {
        path
      }
      priceDisclaimerNode {
        childMarkdownRemark {
          html
          excerpt(format: HTML)
        }
      }
      defaultPathways {
        ...Card
      }
    }
  }
`

export default ProductCollection
